// frontend/src/App.tsx - COMPLETE FIXED VERSION
// All navigation links converted to React Router Links to prevent page reloads

import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, Outlet, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useAppStore } from '@/store/app';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { analytics } from '@/services/analytics';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';

// UI Components
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { NotificationToast } from '@/components/ui/NotificationToast';
import {
  LayoutDashboard, Mail, MessageSquare, Reply, FileText, Paperclip,
  Mailbox, FolderKanban, Users, RefreshCw, Ban, Bell, Settings,
  Building2, User, ClipboardList, Shield
} from 'lucide-react';

// Page Components - Import all pages
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { CorrespondencesPageV3 } from '@/pages/CorrespondencesPageV3'; // V3: New table/grid view
import CorrespondenceDetailPage from '@/pages/CorrespondenceDetailPage'; // Full page detail view
import { ResponsesPage } from '@/pages/ResponsesPage';
import { ResponseDetailPage } from '@/pages/ResponseDetailPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { EmailListPage } from '@/pages/EmailListPage';
import { WorkflowsPage } from '@/pages/WorkflowsPage';
import { StaticDocumentsPage } from '@/pages/StaticDocumentsPage';
import { AttachmentsPage } from '@/pages/AttachmentsPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { EmailAccountsPage } from '@/pages/EmailAccountsPage';
import { ExclusionsPage } from '@/pages/ExclusionsPage';
import { GroupsPage } from '@/pages/GroupsPage';
import { UsersPage } from '@/pages/UsersPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { AdminPanelPage } from '@/pages/AdminPanelPage';
import { AuditLogsPage } from '@/pages/AuditLogsPage';
import { OrgSettingsPage } from '@/pages/OrgSettingsPage';
import { CustomizationPage } from '@/pages/CustomizationPage';
import { IndustryTemplatesPage } from '@/pages/IndustryTemplatesPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Types
import type { UserRole } from '@/api/types';

// ===== UTILITY COMPONENTS =====

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  excludedRoles?: UserRole[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [], 
  excludedRoles = [] 
}) => {
  const { user, isAuthenticated } = useAuthStore();
  
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  
  // Check if user role is excluded
  if (excludedRoles.length > 0 && user.role && excludedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Check if user has required role
  if (requiredRoles.length > 0 && user.role && !requiredRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

// Loading Screen Component
const LoadingScreen: React.FC<{ message?: string }> = ({ message = 'Loading application...' }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="mt-4 text-gray-600 dark:text-gray-400">{message}</p>
    </div>
  </div>
);

// Error Screen Component
const ErrorScreen: React.FC<{ 
  error: string; 
  onRetry: () => void; 
  onClear: () => void; 
}> = ({ error, onRetry, onClear }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
    <div className="max-w-md w-full">
      <ErrorAlert 
        message={error}
        onClose={onClear}
        className="mb-4"
      />
      <div className="text-center">
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
        >
          Try Again
        </button>
        <button
          onClick={onClear}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Continue
        </button>
      </div>
    </div>
  </div>
);

// Notification Container Component
const NotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useAppStore();

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md" role="status" aria-live="polite" aria-label="Notifications">
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => removeNotification(notification.id)}
          persistent={notification.persistent}
          duration={notification.priority === 'high' ? 10000 : notification.priority === 'medium' ? 7000 : 5000}
        />
      ))}
    </div>
  );
};

// Public Layout Component (for login page)
const PublicLayout: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Outlet />
      <NotificationContainer />
    </div>
  );
};

// Dashboard Layout with Sidebar and Header
const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { sidebarOpen, toggleSidebar, theme, setTheme, unreadCount, showOnboarding, setShowOnboarding, completeOnboarding } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Check onboarding status on mount
  useEffect(() => {
    const completed = localStorage.getItem('workidoc_onboarding_completed');
    if (!completed) {
      setShowOnboarding(true);
    }
  }, [setShowOnboarding]);

  const handleLogout = async () => {
    try {
      analytics.reset();
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
  };

  // Track page views on route change
  useEffect(() => {
    analytics.page(location.pathname);
  }, [location.pathname]);

  // Responsive sidebar behavior
  const isDesktop = useBreakpoint('lg');
  useEffect(() => {
    if (!isDesktop) {
      useAppStore.getState().setSidebarOpen(false);
    }
  }, [isDesktop]);

  // Navigation items with role-based access
  interface NavigationItem {
    name: string;
    href: string;
    icon: React.ReactNode;
    roles: UserRole[];
    badge?: number;
  }

  const getNavigationItems = (): NavigationItem[] => {
    const baseItems: NavigationItem[] = [
      { name: 'Dashboard', href: '/dashboard', icon: <LayoutDashboard size={18} aria-hidden="true" />, roles: ['admin', 'contributor', 'implementor'] },
    ];

    // Items only for admin and contributor (NOT implementor)
    const emailManagementItems: NavigationItem[] = [
      { name: 'Emails', href: '/emails', icon: <Mail size={18} aria-hidden="true" />, roles: ['admin', 'contributor'] },
      { name: 'Correspondences', href: '/correspondences', icon: <MessageSquare size={18} aria-hidden="true" />, roles: ['admin', 'contributor'] },
      { name: 'Responses', href: '/responses', icon: <Reply size={18} aria-hidden="true" />, roles: ['admin', 'contributor'] },
      { name: 'Documents', href: '/static-documents', icon: <FileText size={18} aria-hidden="true" />, roles: ['admin', 'contributor'] },
      { name: 'Attachments', href: '/attachments', icon: <Paperclip size={18} aria-hidden="true" />, roles: ['admin', 'contributor'] },
      { name: 'Email Accounts', href: '/email-accounts', icon: <Mailbox size={18} aria-hidden="true" />, roles: ['admin', 'contributor'] },
    ];

    // Items for all roles including implementor
    const managementItems: NavigationItem[] = [
      { name: 'Projects', href: '/projects', icon: <FolderKanban size={18} aria-hidden="true" />, roles: ['admin', 'contributor', 'implementor'] },
      { name: 'Groups', href: '/groups', icon: <Users size={18} aria-hidden="true" />, roles: ['admin', 'contributor', 'implementor'] },
      { name: 'Workflows', href: '/workflows', icon: <RefreshCw size={18} aria-hidden="true" />, roles: ['admin', 'contributor', 'implementor'] },
      { name: 'Exclusions', href: '/exclusions', icon: <Ban size={18} aria-hidden="true" />, roles: ['admin', 'contributor', 'implementor'] },
    ];

    // Notifications - only for admin and contributor
    const notificationItems: NavigationItem[] = [
      { name: 'Notifications', href: '/notifications', icon: <Bell size={18} aria-hidden="true" />, badge: unreadCount, roles: ['admin', 'contributor'] },
    ];

    const allItems = [...baseItems, ...emailManagementItems, ...managementItems, ...notificationItems];

    // Filter items based on user role
    return allItems.filter(item => 
      user && item.roles.includes(user.role)
    );
  };

  // Admin navigation items
  const getAdminNavigationItems = () => {
    if (!user) return [];

    const adminItems = [];

    // Admin Panel - for admin and implementor (shown first)
    if (user.role === 'admin' || user.role === 'implementor') {
      adminItems.push({ name: 'Admin Panel', href: '/admin', icon: <Settings size={18} aria-hidden="true" /> });
    }

    // Organizations - only for implementor (shown second)
    if (user.role === 'implementor') {
      adminItems.push({ name: 'Organizations', href: '/organizations', icon: <Building2 size={18} aria-hidden="true" /> });
    }

    // Users - for admin and implementor (shown third)
    if (user.role === 'admin' || user.role === 'implementor') {
      adminItems.push({ name: 'Users', href: '/users', icon: <User size={18} aria-hidden="true" /> });
    }

    // Audit Logs - for admin and implementor (shown fourth)
    if (user.role === 'admin' || user.role === 'implementor') {
      adminItems.push({ name: 'Audit Logs', href: '/audit-logs', icon: <Shield size={18} aria-hidden="true" /> });
    }

    // Org Settings - for admin only (implementors manage orgs via Organizations page)
    if (user.role === 'admin') {
      adminItems.push({ name: 'Org Settings', href: '/org-settings', icon: <Building2 size={18} aria-hidden="true" /> });
    }

    // Customization - for admin and implementor
    if (user.role === 'admin' || user.role === 'implementor') {
      adminItems.push({ name: 'Customization', href: '/customization', icon: <ClipboardList size={18} aria-hidden="true" /> });
    }

    // Industry Templates - for admin and implementor
    if (user.role === 'admin' || user.role === 'implementor') {
      adminItems.push({ name: 'Industry Templates', href: '/industry-templates', icon: <FileText size={18} aria-hidden="true" /> });
    }

    return adminItems;
  };

  // Helper function to check if current route is active
  const isActiveRoute = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const navigationItems = getNavigationItems();
  const adminNavigationItems = getAdminNavigationItems();

  return (
    <>
    <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md">
      Skip to main content
    </a>
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      {sidebarOpen && (
        <>
          {/* Mobile backdrop */}
          <div
            className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
            role="presentation"
            onClick={() => useAppStore.getState().setSidebarOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') useAppStore.getState().setSidebarOpen(false); }}
          />
          
          {/* Sidebar component */}
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 lg:static lg:inset-0" role="navigation" aria-label="Main navigation">
            <div className="flex flex-col h-full">
              {/* Logo */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">WD</span>
                  </div>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    WorkiDoc
                  </span>
                </div>
                <button
                  onClick={() => useAppStore.getState().setSidebarOpen(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Hide sidebar"
                  aria-label="Close sidebar"
                >
                  ✕
                </button>
              </div>

              {/* Navigation */}
              <div className="flex-1 flex flex-col overflow-y-auto">
                {/* Admin Section - Moved to top */}
                {adminNavigationItems.length > 0 && (
                  <div className="px-4 pt-4 pb-2">
                    <h3 className="px-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Administration
                    </h3>
                    <nav className="mt-2 space-y-1">
                      {adminNavigationItems.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          aria-current={isActiveRoute(item.href) ? 'page' : undefined}
                          className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            isActiveRoute(item.href)
                              ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <span className="mr-3">{item.icon}</span>
                          {item.name}
                        </Link>
                      ))}
                    </nav>
                  </div>
                )}

                {/* Main Navigation */}
                <nav className={`flex-1 px-4 space-y-2 ${adminNavigationItems.length > 0 ? 'pt-2 border-t border-gray-200 dark:border-gray-700 mt-2' : 'py-4'}`}>
                  {navigationItems.map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      aria-current={isActiveRoute(item.href) ? 'page' : undefined}
                      className={`flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActiveRoute(item.href)
                          ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="mr-3">{item.icon}</span>
                        {item.name}
                      </div>
                      {item.badge && item.badge > 0 && (
                        <span role="status" aria-live="polite" className="bg-red-100 text-red-800 text-xs font-medium px-2.5 py-0.5 rounded-full dark:bg-red-900 dark:text-red-300" aria-label={`${item.badge} unread notifications`}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </nav>

                {/* User Section */}
                <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center px-3 py-2">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {user?.username || 'Unknown User'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {user?.role || 'Unknown Role'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-2 space-y-1">
                    <button
                      onClick={toggleTheme}
                      className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="mr-3">{theme === 'light' ? '🌙' : '☀️'}</span>
                      {theme === 'light' ? 'Dark Mode' : 'Light Mode'}
                    </button>
                    <Link
                      to="/profile"
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActiveRoute('/profile')
                          ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span className="mr-3">👤</span>
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActiveRoute('/settings')
                          ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <span className="mr-3">⚙️</span>
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <span className="mr-3">🚪</span>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Floating buttons when sidebar is closed */}
        {!sidebarOpen && (
          <div className="fixed top-4 left-4 z-30 flex items-center gap-2">
            <button
              onClick={toggleSidebar}
              className="p-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-lg"
              title="Show sidebar"
              aria-expanded="false"
            >
              <span className="sr-only">Show sidebar</span>
              ☰
            </button>
            <button
              onClick={toggleTheme}
              className="p-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-lg"
              title="Toggle theme"
            >
              <span className="sr-only">Toggle theme</span>
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
            {unreadCount > 0 && (
              <Link
                to="/notifications"
                className="relative p-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-lg"
                title="Notifications"
              >
                <span className="sr-only">Notifications</span>
                🔔
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" aria-label={`${unreadCount} unread notifications`}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              </Link>
            )}
          </div>
        )}

        {/* Page Content */}
        <main id="main-content" role="main" className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-900">
          <Outlet />
        </main>
      </div>

      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard
          onComplete={completeOnboarding}
          onDismiss={completeOnboarding}
        />
      )}

      {/* Notification Container */}
      <NotificationContainer />
    </div>
    </>
  );
};

// ===== MAIN APP COMPONENT =====

const App: React.FC = () => {
  const { isAuthenticated, user, checkAuth, isLoading: authLoading, error: authError, clearError: clearAuthError } = useAuthStore();
  const { theme, setTheme, globalError: appError, clearGlobalError: clearAppError } = useAppStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize app
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Apply saved theme
        const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
        const newTheme = savedTheme || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
        setTheme(newTheme);

        // Check authentication status
        await checkAuth();

        // Initialize analytics
        analytics.init();
      } catch (error) {
        console.error('App initialization error:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeApp();
  }, [checkAuth, setTheme]);

  // Handle authentication-based redirects
  useEffect(() => {
    if (!isInitialized || authLoading) return;

    const currentPath = location.pathname;
    
    if (isAuthenticated && user) {
      // Identify user for analytics
      analytics.identify({
        id: user._id || user.id || '',
        email: user.email,
        role: user.role,
        organization: typeof user.organization === 'string' ? user.organization : user.organization?._id,
      });

      // Redirect authenticated users away from login page
      if (currentPath === '/login' || currentPath === '/') {
        navigate('/dashboard', { replace: true });
      }
    } else {
      // Redirect unauthenticated users to login (except for public routes)
      if (currentPath !== '/login') {
        navigate('/login', { replace: true });
      }
    }
  }, [isAuthenticated, user, authLoading, isInitialized, location.pathname, navigate]);

  // Show loading state during initialization or auth loading
  if (!isInitialized || authLoading) {
    return <LoadingScreen message="Initializing application..." />;
  }

  // Show error state if there's a critical error
  const criticalError = authError || appError;
  if (criticalError) {
    return (
      <ErrorScreen
        error={criticalError}
        onRetry={async () => {
          clearAuthError();
          clearAppError();
          await checkAuth();
        }}
        onClear={() => {
          clearAuthError();
          clearAppError();
        }}
      />
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout />}>
        <Route 
          path="login" 
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <LoginPage />
            )
          } 
        />
      </Route>

      {/* Protected Routes with Dashboard Layout */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        {/* Main Dashboard */}
        <Route path="dashboard" element={<DashboardPage />} />
        
        {/* Core Features */}
        <Route path="correspondences" element={<CorrespondencesPageV3 />} />
        <Route path="correspondences/:id" element={<CorrespondenceDetailPage />} />
        <Route path="responses" element={<ResponsesPage />} />
        <Route path="responses/:id" element={<ResponseDetailPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:id" element={<ProjectsPage />} />
        
        {/* Email Management */}
        <Route path="emails" element={<EmailListPage />} />
        <Route path="emails/:id" element={<EmailListPage />} />
        <Route path="email-accounts" element={<EmailAccountsPage />} />
        
        {/* Workflow and Automation */}
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route path="workflows/:id" element={<WorkflowsPage />} />
        <Route path="exclusions" element={<ExclusionsPage />} />
        
        {/* Content Management */}
        <Route path="static-documents" element={<StaticDocumentsPage />} />
        <Route path="attachments" element={<AttachmentsPage />} />
        
        {/* Communication */}
        <Route path="notifications" element={<NotificationsPage />} />
        
        {/* User Management */}
        <Route 
          path="users" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor']}>
              <UsersPage />
            </ProtectedRoute>
          } 
        />
        <Route
          path="groups"
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor', 'contributor']}>
              <GroupsPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="organizations" 
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor']}>
              <OrganizationsPage />
            </ProtectedRoute>
          } 
        />
        
        {/* Administration */}
        <Route
          path="admin"
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor']}>
              <AdminPanelPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="audit-logs"
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor']}>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="org-settings"
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor']}>
              <OrgSettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="customization"
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor']}>
              <CustomizationPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="industry-templates"
          element={
            <ProtectedRoute requiredRoles={['admin', 'implementor']}>
              <IndustryTemplatesPage />
            </ProtectedRoute>
          }
        />

        {/* User Profile and Settings */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        
        {/* Default redirect for authenticated users */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        {/* 404 Not Found */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Catch-all redirect for unauthenticated users */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default App;